<template>
  <div
    class="container-scrollbar relative flex max-h-[80vh] w-[320px] flex-col justify-between gap-1 overflow-y-auto rounded-xl bg-white p-3 shadow-normal shadow-gray-400 tablet:w-[360px] desktop:max-h-[calc(100vh-100px)] desktop:w-[400px]"
  >
    <button
      v-if="!isDesktop"
      class="m-2 h-6 w-6"
      :title="$t('header.account.switch')"
      @click="emit('select')"
    >
      <IconArrow direction="left" class="h-full w-full" />
    </button>

    <button
      v-for="account in accounts"
      :key="account.accountId"
      class="rounded-xl p-3 text-left hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:rounded-lg tablet:p-5"
      :disabled="isSubmitting"
      @click="handleSwitch(account)"
    >
      <span class="flex items-start justify-between gap-5">
        <span class="flex-1 text-sm font-bold tablet:text-base">
          {{ account.accountName }}
        </span>
        <span
          class="flex w-20 items-center justify-center gap-1 rounded p-1.5 text-xs"
          :class="provideMemberBadgeClass(account.memberType)"
        >
          <IconAwardVip
            v-if="account.memberType === EnumMemberType.VIP"
            class="h-4 w-4"
          />
          <IconAwardGold
            v-else-if="account.memberType === EnumMemberType.GOLD"
            class="h-4 w-4"
          />
          <IconAwardSliver
            v-else-if="account.memberType === EnumMemberType.SILVER"
            class="h-4 w-4"
          />
          <IconAwardRegular v-else class="h-4 w-4" />
          {{ account.memberType }}
        </span>
      </span>
      <span class="mt-3 block text-xs text-gray-600 tablet:text-sm">
        {{ $t('header.account.balance') }}:
        {{
          provideBalance({ currency: account.currency, point: account.point })
        }}
      </span>
    </button>

    <div class="p-3">
      <TheNuxtLink
        :to="`${consoleDomain}/${locale}/company-accounts`"
        target="_self"
        class="text-sm font-bold text-blue-400 underline hover:text-blue-500 active:text-blue-600 tablet:text-base"
      >
        {{ $t('header.account.view-all-companies') }}
      </TheNuxtLink>
    </div>

    <div
      v-if="isSubmitting"
      class="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white/70"
    >
      <IconLoader class="h-8 w-8" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IAccount } from '@/assets/types/api/CompanyAccounts';

  const emit = defineEmits<{ select: [] }>();

  const { locale } = useI18n();
  const { isDesktop } = useScreen();
  const { consoleDomain } = useWebsite();
  const { provideBalance } = useFinance();
  const { provideMemberBadgeClass } = provideMemberStyle();

  const profileStore = useProfileStore();
  const { profile, companyAccounts } = storeToRefs(profileStore);

  const isSubmitting = ref<boolean>(false);

  const accounts = computed((): IAccount[] => {
    return companyAccounts.value
      .filter((account) => account.accountId !== profile.value.accountId)
      .slice(0, 5);
  });

  const handleSwitch = async (account: IAccount): Promise<void> => {
    if (isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;
    await profileStore.POST_SWITCH_ACCOUNT(account.accountId);
    isSubmitting.value = false;
    emit('select');
  };
</script>
